import { useParams, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Skeleton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useAssetsFromMarket } from '../api/hooks';
import {
    Badge,
    StatBox,
    MiniChart,
    SectionCard,
    PanelHeader,
    PreviewBadge,
} from '../components/shared';
import { ProbabilityChart } from '../components/charts/ProbabilityChart';
import { formatVolume } from '../utils/format';
import { generatePriceData } from '../api/mockData';
import type { MarketFromAsset } from '../api/types';
import { openInNewTab } from '../../../utils/newTab';

// Pre-generate some sparkline data for the related assets table
const SPARKLINE_DATA = generatePriceData(30, 100).map(d => d.close);

export function MarketView() {
    const { id: conditionId } = useParams<{ id: string }>();
    const location = useLocation();
    const theme = useTheme();

    // Get market info from navigation state (set by MarketCard or SearchBar)
    const navState = location.state as {
        question?: string;
        market?: MarketFromAsset;
    } | null;

    const marketInfo = navState?.market;
    const question =
        navState?.question || marketInfo?.question || `Market ${conditionId}`;
    const resolved = marketInfo?.resolved ?? false;
    const outcome = marketInfo?.outcome;
    const prob = marketInfo?.current_yes_prob ?? 0.5;
    const volume = marketInfo?.volume ?? 0;
    const similarity = marketInfo?.similarity;

    const isYes = outcome === 'YES' || outcome === 'yes';

    // Live API data
    const {
        data: assetsData,
        loading: assetsLoading,
        error: assetsError,
    } = useAssetsFromMarket(conditionId || null);

    const relatedAssets = assetsData?.assets || [];

    if (!conditionId) return null;

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    lg: 'minmax(0, 1fr) minmax(0px, 1320px) minmax(0, 1fr)',
                },
                minHeight: '100vh',
            }}
        >
            <Box
                sx={{
                    display: { xs: 'none', lg: 'block' },
                    borderRight: 1,
                    borderColor: 'divider',
                }}
            />

            <Box sx={{ px: 2, py: 2, minWidth: 0 }}>
                {/* Event Header */}
                <SectionCard sx={{ mb: 2 }}>
                    <Box sx={{ p: 2.5, bgcolor: 'action.hover' }}>
                        <Typography
                            sx={{
                                fontSize: '18px',
                                fontWeight: 700,
                                lineHeight: 1.4,
                                mb: 1.5,
                            }}
                        >
                            {question}
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                mb: 1.75,
                            }}
                        >
                            <Badge
                                color={
                                    resolved
                                        ? isYes
                                            ? theme.palette.success.main
                                            : theme.palette.error.main
                                        : theme.palette.warning.main
                                }
                            >
                                {resolved ? `RESOLVED: ${outcome}` : 'OPEN'}
                            </Badge>
                            {volume > 0 && (
                                <Badge color={theme.palette.warning.main}>
                                    VOL: {formatVolume(volume)}
                                </Badge>
                            )}
                            <Badge
                                color={theme.palette.text.secondary}
                                bg={theme.palette.action.hover}
                            >
                                prediction market
                            </Badge>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                borderTop: 1,
                                borderColor: 'divider',
                                pt: 1.75,
                            }}
                        >
                            <StatBox
                                icon={'\u25EF'}
                                label="Start"
                                value={
                                    marketInfo?.start_date
                                        ? new Date(
                                            marketInfo.start_date
                                        ).toLocaleDateString('de-DE')
                                        : '-'
                                }
                            />
                            <Box
                                sx={{
                                    width: 1,
                                    bgcolor: 'divider',
                                    mx: 1,
                                }}
                            />
                            <StatBox
                                icon={'\u25CE'}
                                label="End"
                                value={
                                    marketInfo?.end_date
                                        ? new Date(
                                            marketInfo.end_date
                                        ).toLocaleDateString('de-DE')
                                        : '-'
                                }
                            />
                            <Box
                                sx={{
                                    width: 1,
                                    bgcolor: 'divider',
                                    mx: 1,
                                }}
                            />
                            <StatBox
                                icon="$"
                                label="Volume"
                                value={volume > 0 ? formatVolume(volume) : '-'}
                            />
                            <Box
                                sx={{
                                    width: 1,
                                    bgcolor: 'divider',
                                    mx: 1,
                                }}
                            />
                            <StatBox
                                icon={'\u2713'}
                                label="Current Prob"
                                value={`${(prob * 100).toFixed(0)}%`}
                                valueColor={
                                    resolved
                                        ? isYes
                                            ? theme.palette.success.main
                                            : theme.palette.error.main
                                        : theme.palette.primary.main
                                }
                            />
                        </Box>
                    </Box>
                </SectionCard>

                {/* Probability Chart */}
                <Box sx={{ mb: 2 }}>
                    <ProbabilityChart
                        resolved={resolved}
                        outcome={outcome}
                        prob={prob}
                    />
                </Box>

                {/* Related Assets */}
                <SectionCard sx={{ mb: 2 }}>
                    <PanelHeader title="Related Assets" icon={'\u25A8'}>
                        {assetsLoading && <Skeleton width={60} height={20} />}
                    </PanelHeader>
                    {assetsLoading ? (
                        <Box sx={{ p: 2 }}>
                            {Array.from({ length: 3 }).map((_, i) => (
                                <Skeleton
                                    key={i}
                                    height={48}
                                    sx={{ mb: 0.5 }}
                                />
                            ))}
                        </Box>
                    ) : assetsError ? (
                        <Typography
                            sx={{
                                color: 'error.main',
                                textAlign: 'center',
                                py: 3,
                                fontSize: '13px',
                            }}
                        >
                            Failed to load related assets
                        </Typography>
                    ) : relatedAssets.length > 0 ? (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {['ASSET', 'CHART (30d)', 'SIMILARITY'].map(
                                        h => (
                                            <TableCell
                                                key={h}
                                                sx={{
                                                    fontSize: '10.5px',
                                                    color: 'text.disabled',
                                                    fontWeight: 600,
                                                    letterSpacing: '0.06em',
                                                }}
                                            >
                                                {h}
                                            </TableCell>
                                        )
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {relatedAssets.map(a => (
                                    <TableRow
                                        key={a.ticker}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() =>
                                            openInNewTab(
                                                `/markets/asset/${a.asset_type}/${a.ticker}`
                                            )
                                        }
                                    >
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        fontSize: '13px',
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {a.ticker}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '12px',
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    {a.name}
                                                </Typography>
                                                <Badge
                                                    color={
                                                        theme.palette.primary
                                                            .main
                                                    }
                                                >
                                                    {a.asset_type}
                                                </Badge>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                }}
                                            >
                                                <MiniChart
                                                    data={SPARKLINE_DATA}
                                                    color={
                                                        theme.palette.success
                                                            .main
                                                    }
                                                />
                                                <PreviewBadge />
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontFamily: 'monospace',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                            }}
                                        >
                                            {a.similarity.toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 3,
                                color: 'text.disabled',
                            }}
                        >
                            <Typography sx={{ fontSize: '13px' }}>
                                No related assets found
                            </Typography>
                        </Box>
                    )}
                </SectionCard>

                {/* Scatter Plot Placeholder */}
                <SectionCard>
                    <PanelHeader title="Probability vs Price Correlation" />
                    <Box
                        sx={{
                            p: 4,
                            textAlign: 'center',
                            minHeight: 200,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px dashed',
                            borderColor: 'divider',
                            borderRadius: '4px',
                            m: 2,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '14px',
                                fontWeight: 600,
                                color: 'text.secondary',
                                mb: 1,
                            }}
                        >
                            Coming Soon
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '12px',
                                color: 'text.disabled',
                                maxWidth: 400,
                            }}
                        >
                            Phase scatter plot showing probability vs price
                            correlation. Clockwise motion indicates the market
                            leads the asset price; counter-clockwise indicates
                            it lags.
                        </Typography>
                    </Box>
                </SectionCard>
            </Box>

            <Box
                sx={{
                    display: { xs: 'none', lg: 'block' },
                    borderLeft: 1,
                    borderColor: 'divider',
                }}
            />
        </Box>
    );
}
